/**
 * Which layer each field's value comes from, for the form that owns a layer of its own.
 *
 * The device form and the site form say the same thing one layer apart, so this is parameterised by
 * `ownLayer` rather than written twice: the staging rules are the subtle part and duplicating them
 * is how the two would drift.
 */

import { collectAllFields, Field, FieldValue, FieldValues, same, Schema } from '../schema';
import { BeneathLayer, ConfigLayer, DifferingKeyDetail, LAYER_ORDER } from '../fields/FieldWrapper';
import { configKeysForField, mapConfigToForm, mapFormToConfig } from './configMappers';

export type OwnLayer = 'device' | 'site';

/** The values each readable layer holds, by layer. Absent means the form could not read that one. */
export type BeneathValues = Partial<Record<ConfigLayer, FieldValues>>;

/**
 * The layers beneath a form's own that come as values, narrowest first.
 *
 * `default` is excluded on purpose: it arrives with the form definition, on the fields themselves,
 * rather than as a bag of values like the others.
 */
export const beneathLayers = (ownLayer: ConfigLayer): BeneathLayer[] =>
  LAYER_ORDER.slice(LAYER_ORDER.indexOf(ownLayer) + 1).filter(
    (layer): layer is BeneathLayer => layer !== 'default' && layer !== 'device'
  );

/** The option a synthetic control shows for a configuration, which its parameters decide together. */
const deriveSynthetic = (field: Field, config: FieldValues): FieldValue => mapConfigToForm(config)[field.id];

/**
 * What each layer reads as for a synthetic control, or nothing where they cannot be worked out.
 *
 * Needs the parameters' own defaults, which only reach the form on the field itself: they have no
 * control of their own here, so `field.default` names none of them.
 */
const syntheticLayers = (
  field: Field,
  keys: string[],
  ownLayer: OwnLayer,
  ownLayerValues: FieldValues,
  beneathValues: BeneathValues
): Partial<Record<ConfigLayer, FieldValue>> | undefined => {
  if (!field.synthetic || !field.source_defaults) return undefined;

  const derive = (config: FieldValues): FieldValue => deriveSynthetic(field, config);
  // A layer holds a parameter or it does not; an absent one must not read as a value, or the layer
  // beneath would be overwritten with nothing.
  const held = (values?: FieldValues): FieldValues =>
    Object.fromEntries(keys.filter((key) => values?.[key] !== undefined).map((key) => [key, values![key]]));

  const defaults: FieldValues = { ...field.source_defaults };
  const resolved: Partial<Record<ConfigLayer, FieldValue>> = { default: derive(defaults) };

  // Widest first, so each layer derives from everything beneath it rather than from the defaults
  // alone -- a mode is decided by all of its parameters together, wherever each one comes from.
  let accumulated: FieldValues = { ...defaults };
  for (const layer of [...beneathLayers(ownLayer)].reverse()) {
    accumulated = { ...accumulated, ...held(beneathValues[layer]) };
    // Only where that layer holds one of the parameters. Named from what is beneath it alone, it
    // would read as displacing a value the layer never set.
    if (keys.some((key) => beneathValues[layer]?.[key] !== undefined)) resolved[layer] = derive(accumulated);
  }
  resolved[ownLayer] = derive({ ...accumulated, ...held(ownLayerValues) });

  return resolved;
};

export interface LayerDetailInput {
  schema: Schema;
  ownLayer: OwnLayer;
  /** The layer this form writes, unseeded by defaults: exactly the keys it holds. */
  ownLayerValues: FieldValues;
  /** Every layer beneath this form's own. The site form has none and passes an empty record. */
  beneathValues: BeneathValues;
  /** What the device actually runs. Only consulted to spot a value no layer accounts for. */
  configValues: FieldValues;
  clearedKeys: string[];
  pinnedKeys: string[];
}

/**
 * Where each value comes from: this form's own layer, the site's where the device form can see one,
 * or -- claimed by neither -- the device type's default. Decided by which layer holds the key, never
 * by comparing against a default value, because the schema's `default` can be a display value the
 * device never runs.
 *
 * The configuration service layers a resident's settings between the site and the device. This
 * reads two layers so far, so a value from that one still falls through to the drift branch below;
 * `LAYER_ORDER` is where it joins.
 */
export const buildLayerDetail = ({
  schema,
  ownLayer,
  ownLayerValues,
  beneathValues,
  configValues,
  clearedKeys,
  pinnedKeys,
}: LayerDetailInput): Map<string, DifferingKeyDetail[]> => {
  const map = new Map<string, DifferingKeyDetail[]>();
  const below = beneathLayers(ownLayer);

  for (const field of collectAllFields(schema)) {
    // Device model attributes (phone_number) are not configuration parameters, so no layer
    // resolves them.
    if (field.device_model_level) continue;
    const keys = configKeysForField(field);
    const stored = keys.filter((key) => ownLayerValues[key] !== undefined);
    // One entry rather than one per parameter wherever the layers can be derived, since the value on
    // screen belongs to all of them together.
    const layers = syntheticLayers(field, keys, ownLayer, ownLayerValues, beneathValues);
    // What sits under one key, by layer. The defaults come off the field rather than out of a
    // layer, and only for its own key: the fallbacks `defaultForType` invents where the schema
    // gives none are the form's, not the device's.
    const beneathOf = (key: string): Partial<Record<ConfigLayer, FieldValue>> => {
      const out: Partial<Record<ConfigLayer, FieldValue>> = {
        default: key === field.id ? field.default : undefined,
      };
      for (const layer of below) out[layer] = beneathValues[layer]?.[key];
      return out;
    };

    if (!stored.length) {
      // What sits under a key this layer does not hold. Staged or not, it is what the field would
      // resolve to, and it is the whole reason the stack is worth opening.
      const stagedDetail = (source: 'own-cleared' | 'own-will-set'): DifferingKeyDetail[] =>
        layers
          ? [
              {
                key: field.id,
                values: [],
                source,
                layer: ownLayer,
                derived: true,
                override: { ...layers, [ownLayer]: undefined },
              },
            ]
          : keys.map((key) => ({ key, values: [], source, layer: ownLayer, override: beneathOf(key) }));

      // Staged for a null the layer never held: the drift reset, which sends one purely to make
      // the service recollate. Checked first, or it would fall through to the warning it was
      // pressed from.
      if (keys.some((key) => clearedKeys.includes(key))) {
        map.set(field.id, stagedDetail('own-cleared'));
        continue;
      }
      // Staged to join the layer. Nothing is stored yet, but what it would otherwise inherit is
      // exactly what the user is deciding to replace.
      if (keys.some((key) => pinnedKeys.includes(key))) {
        map.set(field.id, stagedDetail('own-will-set'));
        continue;
      }
      // Nothing of this form's own, so the value comes from the nearest layer beneath that holds
      // any of the keys. Narrowest first, because that is the one an edit here would displace. A
      // form with no layer beneath finds none, which is what an unmarked field means there.
      const inheritedFrom = below.find((layer) => keys.some((key) => beneathValues[layer]?.[key] !== undefined));
      const fromBeneath = inheritedFrom ? keys.filter((key) => beneathValues[inheritedFrom]?.[key] !== undefined) : [];
      if (inheritedFrom && fromBeneath.length) {
        map.set(
          field.id,
          // Carries the stack even though nothing of this form's own is in it: the device runs the
          // site's value, and what it would fall back to is worth being able to see.
          layers
            ? [
                {
                  key: field.id,
                  values: [],
                  source: 'inherited' as const,
                  inheritedFrom,
                  layer: ownLayer,
                  derived: true,
                  override: { ...layers, [ownLayer]: undefined },
                },
              ]
            : fromBeneath.map((key) => ({
                key,
                values: [],
                source: 'inherited' as const,
                inheritedFrom,
                layer: ownLayer,
                override: beneathOf(key),
              }))
        );
        continue;
      }

      // A value no layer the form can read accounts for, and not the type default either: the site
      // cleared the key, and the change was never sent to the devices. Pushing the site layer is a
      // separate and optional action, so a device keeps running the value the site used to set
      // long after the site stopped setting it. The device really does run it, so presenting it
      // unmarked would be claiming it is the default.
      //
      // A resident's settings surface here too, for the same reason and wrongly: the service does
      // write that layer, this form just cannot read it yet, so a value from it reads as a device
      // running something nobody set. Reading it is what stops that -- not a special case here.
      //
      // What it runs against what it should inherit, which for a synthetic control is a mode on
      // either side rather than a parameter. Both have to be readable: with no default there is
      // nothing to differ from, and a parameter missing from the collated configuration would make
      // a mode derive to the wrong thing -- either way the value cannot be told apart from an
      // ordinary inherited one, so it is left unmarked.
      const running = layers ? deriveSynthetic(field, configValues) : configValues[field.id];
      const inherited = layers ? layers.default : field.default;
      const readable = layers ? keys.every((key) => configValues[key] !== undefined) : running !== undefined;
      // `below.length` matters as much as `every`: a form with nothing between its own layer and
      // the defaults has no layer that could be accounting for the value, so there is no drift to
      // report -- and an `every` over an empty list would quietly say the opposite.
      if (
        below.length > 0 &&
        below.every((layer) => beneathValues[layer] !== undefined) &&
        inherited !== undefined &&
        readable &&
        !same(running, inherited)
      ) {
        map.set(field.id, [
          {
            key: field.id,
            values: [],
            source: 'drifted' as const,
            layer: ownLayer,
            derived: layers !== undefined,
            override: { [ownLayer]: running, default: inherited },
          },
        ]);
      }
      continue;
    }

    // Precedence rather than a tally: a synthetic field whose keys come from different layers is
    // named after the narrowest one holding any of them, which is the one an edit here displaces.
    // Every stored key on its way out reads as cleared; partially cleared reads as stored, which is
    // honest, since part of it is still in the layer.
    const source = stored.every((key) => clearedKeys.includes(key)) ? 'own-cleared' : 'own';
    if (layers) {
      map.set(field.id, [{ key: field.id, values: [], source, layer: ownLayer, derived: true, override: layers }]);
      continue;
    }
    map.set(
      field.id,
      // The stack this layer sits on, so the field can name what it displaces. A layer stays absent
      // where it holds nothing -- that absence is what makes the field say it displaces the default
      // rather than a site value.
      stored.map((key) => ({
        key,
        values: [],
        source: source as 'own' | 'own-cleared',
        layer: ownLayer,
        // Own layer last: `beneathOf` only ever names layers beneath this one, so the two cannot
        // collide today -- but a literal key overwriting the computed one is how the site form's
        // synthetic fields lost their own value once already.
        override: { ...beneathOf(key), [ownLayer]: ownLayerValues[key] },
      }))
    );
  }

  return map;
};

/**
 * What a selection diverges from, for the bulk form -- the one form with no layer of its own. Where
 * a form has one, `buildLayerDetail` says this and more, and two flags on one field would be saying
 * it twice.
 */
export const buildSiteDetail = (
  schema: Schema,
  siteConfigValues: FieldValues,
  values: FieldValues
): Map<string, DifferingKeyDetail[]> => {
  const allFields = collectAllFields(schema);
  const current = mapFormToConfig(values, allFields);
  const map = new Map<string, DifferingKeyDetail[]>();

  for (const field of allFields) {
    const diverging = configKeysForField(field)
      .filter((key) => siteConfigValues[key] !== undefined && !same(current[key], siteConfigValues[key]))
      // No mac: the site is the one source, and naming it would say nothing.
      .map((key) => ({ key, values: [{ value: siteConfigValues[key] }] }));
    if (diverging.length) map.set(field.id, diverging);
  }

  return map;
};
